import type { ClusterNode } from '../../api/types';

/**
 * Which nodes the registry screen is showing.
 *
 * Two states and their union, because two is all a node has: the registry
 * records when a node joined and when its renewal was closed, and nothing else
 * about its condition. Whether its tunnel is up right now is not in here —
 * the cluster publishes no such signal, so a tab claiming to show it would be
 * inventing one.
 */
export type NodeFilter = 'all' | 'active' | 'revoked';

export const NODE_FILTERS: readonly NodeFilter[] = ['all', 'active', 'revoked'];

/** Whether this node's renewal is still open. */
export function isActive(node: ClusterNode): boolean {
  return node.revoked_at === undefined;
}

/**
 * The tab a person lands on.
 *
 * Active, not all. A cluster a year old has been through twenty rollouts, and
 * every one of them left a revoked row behind: the list somebody opens is
 * mostly history, and the handful of nodes that are actually serving is
 * somewhere in it. History is a tab away and stays a tab away.
 */
export const DEFAULT_NODE_FILTER: NodeFilter = 'active';

/**
 * When a node last changed state — enrolled, or revoked.
 *
 * Two different instants deliberately compared as one: on the active tab
 * nothing is revoked so this is the join, and on the revoked tab the join is
 * the uninteresting half. What a person is looking for in both is the row that
 * moved most recently.
 */
function movedAt(node: ClusterNode): number {
  return node.revoked_at ?? node.enrolled_at;
}

/**
 * The nodes one tab shows, newest first.
 *
 * Sorted here rather than left in the cluster's order: that order is the
 * registry's own and puts the oldest node — enrolled when the tenancy was set
 * up and long since replaced — at the top of every list.
 */
export function nodesFor(filter: NodeFilter, nodes: readonly ClusterNode[]): ClusterNode[] {
  const shown =
    filter === 'active'
      ? nodes.filter(isActive)
      : filter === 'revoked'
        ? nodes.filter((node) => !isActive(node))
        : [...nodes];
  return shown.sort((a, b) => movedAt(b) - movedAt(a));
}

/** How many each tab would show, for the counts on the buttons. */
export function nodeCounts(nodes: readonly ClusterNode[]): Record<NodeFilter, number> {
  const active = nodes.filter(isActive).length;
  return { all: nodes.length, active, revoked: nodes.length - active };
}
